import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type UserRole = 'administrator' | 'coach' | 'parent' | 'player';

interface Profile {
  id: string;
  role: UserRole;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signInWithOtp: (email: string, turnstileToken: string) => Promise<void>;
  signInWithPassword: (email: string, password: string, turnstileToken: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string, turnstileToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  fetchProfile: (userId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const fetchedUserId = useRef<string | null>(null); // Keep track of the last fetched user ID

  useEffect(() => {
    let isMounted = true;
    console.log('[AuthContext] Initializing AuthProvider, fetching session...');

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('[AuthContext] Initial getSession result:', session ? `User: ${session.user.id}` : 'No session');
      if (!isMounted) return;
      
      const initialUser = session?.user ?? null;
      setUser(initialUser);
      
      if (initialUser && fetchedUserId.current !== initialUser.id) {
        console.log(`[AuthContext] Initial session has user ${initialUser.id}, fetching profile.`);
        fetchedUserId.current = initialUser.id;
        fetchProfile(initialUser.id);
      } else if (!initialUser) {
        console.log('[AuthContext] Initial session has no user.');
        setProfile(null); // Ensure profile is null if no user initially
        setLoading(false);
      } else {
        console.log(`[AuthContext] Initial session user ${initialUser.id} already processed or fetching.`);
        // If user is present but profile fetch wasn't needed, ensure loading is false.
        // This might happen on fast refresh or if fetchProfile finishes before listener setup?
         if (!loading) setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('[AuthContext] onAuthStateChange triggered. Event:', _event, 'Session:', session ? `User: ${session.user?.id}`: 'No Session');
      if (!isMounted) return;

      const newUser = session?.user ?? null;
      const newUserId = newUser?.id;
      const currentUserId = fetchedUserId.current; // Use ref to check last fetched ID

      setUser(newUser); // Update user state immediately

      if (newUser) {
        // Fetch profile only if the user ID is different from the last one we fetched for
        if (newUserId && newUserId !== currentUserId) {
            console.log(`[AuthContext] Auth state changed to new user ${newUserId}. Fetching profile.`);
            fetchedUserId.current = newUserId; // Update ref *before* calling fetch
            fetchProfile(newUserId);
        } else if (newUserId && newUserId === currentUserId) {
             console.log(`[AuthContext] Auth state changed, but user ${newUserId} is the same as last fetched. Profile should be loaded or loading. Current loading state: ${loading}`);
             // If user is the same but somehow profile is null, maybe refetch? Or trust existing state.
             // For now, assume profile is loading or loaded. Ensure loading is false if it wasn't already.
             // This handles cases like TOKEN_REFRESHED where user doesn't change.
              if (!loading && profile?.id !== newUserId) {
                 console.warn(`[AuthContext] User ${newUserId} is the same, but profile (${profile?.id}) doesn't match or is null. Refetching.`);
                 fetchProfile(newUserId);
              } else if (!loading) {
                  // If not loading and profile seems correct, ensure loading is false.
                  // setLoading(false); // Already handled by fetchProfile's finally/end logic
              }
        }
      } else {
        // User is null (SIGNED_OUT)
        console.log('[AuthContext] Auth state changed to no user. Clearing profile.');
        setProfile(null);
        fetchedUserId.current = null; // Clear ref
        setLoading(false); // Ensure loading is false on sign out
      }
    });

    return () => {
      isMounted = false;
      console.log('[AuthContext] Unsubscribing from onAuthStateChange.');
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Keep dependencies minimal for stable listener


  // fetchProfile is defined outside useEffect, so it retains access to the latest state setters (setProfile, setLoading)
  // but relies on the passed userId argument, not the potentially stale 'user' state variable from its definition scope.
  async function fetchProfile(userId: string) {
    console.log(`[AuthContext] fetchProfile called for userId: ${userId}`);
    setLoading(true); // Set loading true when starting fetch

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, role, first_name, last_name, phone_number, email')
        .eq('id', userId)
        .single();

      console.log('[AuthContext] Profile fetch response:', { data, error });

      if (error) {
        // Handle specific errors, e.g., profile not found (PGRST116) might mean sign-up isn't complete
        if (error.code === 'PGRST116') {
            console.warn(`[AuthContext] Profile not found for user ${userId} (PGRST116). Setting profile null.`);
            setProfile(null); // Explicitly set profile to null
        } else {
            console.error('[AuthContext] Error fetching profile:', error);
            setProfile(null); // Clear profile on other errors too
        }
        // Do not return here, let finally block handle loading state
      } else if (data) {
        // --- REMOVED STALE CHECK ---
        // If fetch was successful (no error and data exists), set the profile.
        // We trust that this fetch was initiated for the correct, current user ID by onAuthStateChange.
        console.log(`[AuthContext] SUCCESS: Setting profile for fetched userId ${userId}:`, data);
        setProfile(data);
      } else {
         // Case where error is null but data is also null/undefined (shouldn't happen with .single())
         console.warn(`[AuthContext] Profile fetch for ${userId} returned no error but no data. Setting profile null.`);
         setProfile(null);
      }
    } catch (catchError) {
        // Catch unexpected errors during the fetch process
        console.error('[AuthContext] Unexpected error in fetchProfile:', catchError);
        setProfile(null);
    } finally {
        // Ensure loading is set to false regardless of success or error
        console.log(`[AuthContext] fetchProfile finished for ${userId}. Setting loading false.`);
        setLoading(false);
    }
  }

  const signInWithOtp = async (email: string, turnstileToken: string) => {
    console.log(`[AuthContext] signInWithOtp called for email: ${email}`);
    const redirectTo = `${window.location.origin}/auth/callback`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
        captchaToken: turnstileToken
      },
    });
    if (error) {
        console.error('[AuthContext] signInWithOtp error:', error);
        throw error;
    }
    console.log(`[AuthContext] OTP sent successfully to ${email}`);
  };

  const signInWithPassword = async (email: string, password: string, turnstileToken: string) => {
    console.log(`[AuthContext] signInWithPassword called for email: ${email}`);
    // Reset profile state before sign-in attempt? Optional, maybe helps avoid flicker.
    // setProfile(null);
    // setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: {
        captchaToken: turnstileToken
      }
    });
    if (error) {
        console.error('[AuthContext] signInWithPassword error:', error);
        setLoading(false); // Ensure loading is false on error
        throw error;
    }
    console.log(`[AuthContext] Password sign in successful for ${email}. Waiting for onAuthStateChange...`);
    // onAuthStateChange will handle setting user and triggering fetchProfile
  };

  const signUpWithPassword = async (email: string, password: string, turnstileToken: string) => {
    console.log(`[AuthContext] signUpWithPassword called for email: ${email}`);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        captchaToken: turnstileToken
      }
    });
    
    if (error) {
        console.error('[AuthContext] signUpWithPassword error:', error);
        throw error;
    }

    console.log(`[AuthContext] Sign up successful for ${email}. Result:`, data);
    // Note: Supabase signUp might automatically sign in the user or require email confirmation.
    // onAuthStateChange should handle the user state update if auto-signin occurs.
  };

  const signOut = async () => {
    console.log('[AuthContext] signOut called.');
    const { error } = await supabase.auth.signOut();
    // No need to manually set user/profile to null here,
    // onAuthStateChange will trigger with a null session and handle it.
    if (error) {
        console.error('[AuthContext] signOut error:', error);
        // If sign out fails, states might remain, which is arguably correct.
        throw error;
    }
    console.log('[AuthContext] Sign out initiated. Waiting for onAuthStateChange...');
  };

  // Log state changes for debugging renders
  useEffect(() => {
      console.log('[AuthContext] State updated:', { user: user?.id, profile: profile?.id, loading });
  }, [user, profile, loading]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      loading, 
      signInWithOtp, 
      signInWithPassword, 
      signUpWithPassword, 
      signOut, 
      fetchProfile // Expose fetchProfile if needed elsewhere, but usually not called directly
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}